import { Box, Flex, Image, Table, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react";
import { useContext } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { PublicationVersionFileModel } from "@nens/rana/api-v1";
import { RanaButton, RanaModal } from "@nens/ranalib";

import exclemationMarks from "@/images/exclemation-marks.png";
import { ProcessContext } from "@/lib/process-provider/process-provider";
import { ProjectContext } from "@/stores/project-provider";
import { getNameFromFileId } from "@/utils/get-name-from-file-id";

interface FileAddedScenarioModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddScenarioFile: () => void;
  scenarioFiles: PublicationVersionFileModel[];
}

const getProcessName = ({ fileName }: { fileName: string }) => {
  return `Compute max waterdepth for ${fileName}`;
};

const getMaxWaterdepthId = ({ fileId }: { fileId: string }) => {
  return `${fileId.split(".")[0]}-max-water-depth.tif`;
};

export function FileAddedScenarioModal({
  isOpen,
  onClose,
  onAddScenarioFile,
  scenarioFiles,
}: FileAddedScenarioModalProps) {
  const project = useContext(ProjectContext);
  const intl = useIntl();
  const { startRun } = useContext(ProcessContext);

  const onComputeMaxWaterDepth = async () => {
    await Promise.all(
      scenarioFiles.map(async (file) => {
        const processName = getProcessName({ fileName: getNameFromFileId(file.id) });
        await startRun({
          project,
          processCompleteMessage: intl.formatMessage(
            {
              id: "publication.file_added_scenario.max_waterdepth_computed",
              defaultMessage:
                "Done with process: {processName}. You can now add the max waterdepth file to the publication.",
            },
            { processName }
          ),
          values: {
            tag: "compute_max_waterdepth",
            name: processName,
            inputs: {
              scenario: {
                id: file.id,
              },
            },
            outputs: {
              maxWaterDepthRaster: {
                id: getMaxWaterdepthId({ fileId: file.id }),
              },
            },
          },
        });
      })
    );
    onClose();
  };

  return (
    <RanaModal
      isOpen={isOpen}
      onClose={onClose}
      topBar={
        <FormattedMessage
          id="publication.file_added_scenario.title"
          defaultMessage="You have added a scenario file"
        />
      }
      bottomChild={
        <Text color="brand.clear_creek" fontStyle="italic" fontSize="sm" pr={4}>
          <FormattedMessage
            id="publication.file_added_scenario.explanation"
            defaultMessage="The max waterdepth file will be added to the same folder as the scenario file"
          />
        </Text>
      }
      maxW="4xl"
      buttons={[
        <RanaButton key="compute-max-waterdepth" variant="primary" onClick={onComputeMaxWaterDepth}>
          <FormattedMessage
            id="publication.file_added_scenario.compute"
            defaultMessage="Compute max waterdepth →"
          />
        </RanaButton>,
        {
          children: (
            <FormattedMessage
              id="publication.file_added_scenario.add_file"
              defaultMessage="Add scenario file"
            />
          ),
          variant: "secondary",
          onClick: () => {
            onAddScenarioFile();
            onClose();
          },
        },
      ]}
    >
      <Flex mb={4} gap={16} alignItems="space-between" justifyContent="space-between">
        <Box maxW="md" flexGrow="2">
          <Text fontWeight={300}>
            <Text as="strong">
              <FormattedMessage
                id="publication.file_added_scenario.warning"
                defaultMessage="Publications do not support editing scenario files in the Rana Desktop client"
              />
            </Text>
          </Text>
          <Text mt={2} fontWeight={300}>
            <FormattedMessage
              id="publication.file_added_scenario.explanation"
              defaultMessage="If you want to make adjustments to the max water depth in the Rana desktop Client you'll have to compute the max water depth first."
            />
          </Text>
        </Box>
        <Box maxW="2xs" flexShrink={1}>
          <Image src={exclemationMarks} alt="Exclamation Marks" />
        </Box>
      </Flex>
      <Box>
        <Table variant="grid">
          <Thead>
            <Tr>
              <Th>
                <FormattedMessage
                  id="publication.file_added_scenario.table_header_file_name"
                  defaultMessage="File name"
                />
              </Th>
              <Th>
                <FormattedMessage
                  id="publication.file_added_scenario.table_header_process_name"
                  defaultMessage="Process name"
                />
              </Th>
              <Th>
                <FormattedMessage
                  id="publication.file_added_scenario.table_header_max_waterdepth"
                  defaultMessage="Max waterdepth file name"
                />
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {scenarioFiles.map((file) => (
              <Tr key={file.id}>
                <Td>{getNameFromFileId(file.id)}</Td>
                <Td>
                  {getProcessName({
                    fileName: getNameFromFileId(file.id),
                  })}
                </Td>
                <Td>{getNameFromFileId(getMaxWaterdepthId({ fileId: file.id }))}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </RanaModal>
  );
}
